import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { cleanRut, validateRut, formatRut } from 'rutlib/lib';
import { AlertHelper } from 'src/app/shared/components/helpers/alert.helpers';
import { EntregaEquipoLinkService } from 'src/app/pages/services/entrega-equipo-link.service';
import { EquipoTecnologicoService } from 'src/app/pages/services/equipo-tecnologico.service';
import { TrabajadoresService } from 'src/app/pages/services/trabajadores/trabajadores.service';
import { EquipoTecnologico } from 'src/app/shared/models/equipo-tecnologico.interface';
import { GenerarEntregaEquipoLinkPayload, TipoMovimientoEntregaEquipo } from 'src/app/shared/models/entrega-equipo-link.interface';

@Component({
  selector: 'app-generar-entrega-equipos',
  templateUrl: './generar-entrega-equipos.component.html',
  styleUrls: ['./generar-entrega-equipos.component.css']
})
export class GenerarEntregaEquiposComponent implements OnInit {

  @Output() linkGenerado_evento: EventEmitter<boolean> = new EventEmitter();

  tiposMovimiento: TipoMovimientoEntregaEquipo[] = ['ENTREGA', 'DEVOLUCION', 'REEMPLAZO'];

  form: {
    rut: string;
    esExtranjero: boolean;
    tipoMovimiento: TipoMovimientoEntregaEquipo;
    observacion: string;
  } = {
    rut: '',
    esExtranjero: false,
    tipoMovimiento: 'ENTREGA',
    observacion: ''
  };

  // Búsqueda de equipo del catálogo (opcional)
  textoBusquedaEquipo: string = '';
  equiposEncontrados: EquipoTecnologico[] = [];
  equipoSeleccionado: EquipoTecnologico | null = null;
  buscadorDebouncer: Subject<string> = new Subject();

  linkGenerado: string = '';
  generando: boolean = false;

  // Registro manual: el documento ya fue firmado en papel, solo se sube el respaldo escaneado
  esDocumentoManual: boolean = false;
  hojaFisicaFile: File | null = null;
  registrandoManual: boolean = false;

  // Registro forzado: el trabajador se niega a firmar (ej. salida conflictiva), el admin
  // cierra el movimiento igual dejando constancia del motivo, sin firma ni documento.
  esRegistroForzado: boolean = false;
  motivoSinFirma: string = '';
  registrandoForzado: boolean = false;

  // Búsqueda de trabajador existente por rut (solo informativo, no bloquea el generar)
  buscandoTrabajador: boolean = false;
  trabajadorEncontrado: { id: number; nombres: string; apellidos: string } | null = null;
  trabajadorBuscado: boolean = false;

  // Coincidencias en vivo mientras se escribe el rut (mismo patrón que lista-implementos)
  trabajadoresCache: any[] = [];
  sugerenciasTrabajador: any[] = [];

  // Solo si tipoMovimiento = REEMPLAZO: equipo activo del trabajador que se va a devolver
  equiposActivosDelTrabajador: any[] = [];
  equipoAnteriorSeleccionado: any | null = null;

  constructor(
    private alert: AlertHelper,
    private entregaEquipoLinkService: EntregaEquipoLinkService,
    private equipoTecnologicoService: EquipoTecnologicoService,
    private trabajadoresService: TrabajadoresService
  ) { }

  ngOnInit(): void {
    this.buscadorDebouncer
      .pipe(debounceTime(400))
      .subscribe(texto => this.buscarEquipo(texto));

    this.trabajadoresService.TraerTrabajadores().subscribe(res => {
      if (res.success) {
        this.trabajadoresCache = res.data || [];
      }
    });
  }

  // Formatea el rut con puntos/guion mientras se escribe (igual que agregar-trabajador)
  // y muestra coincidencias en vivo (igual que la búsqueda de lista-implementos).
  onRutInput(event: Event) {
    const valor = (event.target as HTMLInputElement).value;
    this.form.rut = formatRut(valor);
    this.trabajadorEncontrado = null;
    this.trabajadorBuscado = false;

    const textoLimpio = cleanRut(valor);
    if (!textoLimpio || textoLimpio.length < 3) {
      this.sugerenciasTrabajador = [];
      return;
    }
    this.sugerenciasTrabajador = this.trabajadoresCache
      .filter((t: any) => t.rut && t.rut.toString().includes(textoLimpio))
      .slice(0, 8);
  }

  seleccionarTrabajador(trabajador: any) {
    this.form.rut = formatRut(trabajador.rut.toString());
    this.trabajadorEncontrado = { id: trabajador.id, nombres: trabajador.nombres, apellidos: trabajador.apellidos };
    this.trabajadorBuscado = true;
    this.sugerenciasTrabajador = [];
    this.cargarEquiposActivosSiCorresponde();
  }

  onTipoMovimientoChange() {
    this.equipoAnteriorSeleccionado = null;
    this.equiposActivosDelTrabajador = [];
    this.equipoSeleccionado = null;
    this.cargarEquiposActivosSiCorresponde();
  }

  get requiereSeleccionRestringida(): boolean {
    return this.form.tipoMovimiento === 'REEMPLAZO' || this.form.tipoMovimiento === 'DEVOLUCION';
  }

  private cargarEquiposActivosSiCorresponde() {
    // Se carga siempre que haya trabajador (no solo para reemplazo/devolucion): también
    // se usa para advertir en ENTREGA si ya tiene un equipo activo del mismo tipo.
    if (!this.trabajadorEncontrado) {
      this.equiposActivosDelTrabajador = [];
      return;
    }
    this.entregaEquipoLinkService.listarEquiposActivosDeTrabajador(this.trabajadorEncontrado.id).subscribe(res => {
      this.equiposActivosDelTrabajador = res.data || [];
    });
  }

  seleccionarEquipoAnterior(equipo: any) {
    this.equipoAnteriorSeleccionado = equipo;
  }

  esEquipoActivoSeleccionado(equipoActivo: any): boolean {
    if (this.form.tipoMovimiento === 'REEMPLAZO') {
      return this.equipoAnteriorSeleccionado === equipoActivo;
    }
    return !!this.equipoSeleccionado && this.equipoSeleccionado.id === equipoActivo.fk_equipoTecnologico;
  }

  // Para DEVOLUCION: el equipo elegido de la lista restringida ES el equipo a devolver,
  // se guarda en equipoSeleccionado (mismo campo que usa el payload para fk_equipoTecnologico).
  seleccionarEquipoParaDevolver(equipoActivo: any) {
    this.equipoSeleccionado = {
      id: equipoActivo.fk_equipoTecnologico,
      tipo: equipoActivo.EquipoTecnologico?.tipo,
      marca: equipoActivo.EquipoTecnologico?.marca,
      modelo: equipoActivo.EquipoTecnologico?.modelo,
      numeroSerie: equipoActivo.EquipoTecnologico?.numeroSerie
    } as EquipoTecnologico;
  }

  onBuscarEquipo(event: Event) {
    const texto = (event.target as HTMLInputElement).value;
    this.buscadorDebouncer.next(texto);
  }

  private buscarEquipo(texto: string) {
    if (!texto || texto.trim().length === 0) {
      this.equiposEncontrados = [];
      return;
    }
    this.equipoTecnologicoService.buscarEquipos(texto.trim()).subscribe(res => {
      this.equiposEncontrados = res.data || [];
    });
  }

  seleccionarEquipo(equipo: EquipoTecnologico) {
    this.equipoSeleccionado = equipo;
    this.equiposEncontrados = [];
    this.textoBusquedaEquipo = '';

    // Advertencia (no bloquea): si este equipo ya está activo en otro trabajador, avisa
    // para evitar que quede "entregado" a dos personas a la vez por error.
    this.entregaEquipoLinkService.verificarSerieActiva(equipo.id).subscribe(res => {
      if (res.success && res.data?.activo && res.data?.fk_trabajador && res.data.fk_trabajador !== this.trabajadorEncontrado?.id) {
        this.alert.error_small(`Atención: este equipo ya está activo (entregado) en otro trabajador (id ${res.data.fk_trabajador}).`);
      }
    });

    // Advertencia (no bloquea): si es ENTREGA (no reemplazo) y el trabajador ya tiene un
    // equipo activo del mismo tipo, probablemente debería ser un REEMPLAZO en vez de ENTREGA.
    if (this.form.tipoMovimiento === 'ENTREGA') {
      const yaTieneEsteTipo = this.equiposActivosDelTrabajador.some((a: any) => a.EquipoTecnologico?.tipo === equipo.tipo);
      if (yaTieneEsteTipo) {
        this.alert.error_small(`Atención: este trabajador ya tiene un ${equipo.tipo} activo. ¿Seguro que no debería ser un REEMPLAZO?`);
      }
    }
  }

  quitarEquipoSeleccionado() {
    this.equipoSeleccionado = null;
  }

  buscarTrabajadorPorRut() {
    if (!this.form.rut || this.form.rut.trim().length === 0) {
      this.alert.error_small('Ingrese un RUT antes de buscar.');
      return;
    }
    const rutLimpio = cleanRut(this.form.rut);
    this.buscandoTrabajador = true;
    this.trabajadorEncontrado = null;
    this.trabajadorBuscado = false;
    this.trabajadoresService.traerTrabajadoRut(rutLimpio).subscribe({
      next: (respuesta) => {
        this.buscandoTrabajador = false;
        this.trabajadorBuscado = true;
        if (respuesta?.success && Array.isArray(respuesta.data) && respuesta.data.length > 0) {
          const ultimoTrabajador = respuesta.data[respuesta.data.length - 1];
          this.trabajadorEncontrado = {
            id: ultimoTrabajador.id,
            nombres: ultimoTrabajador.nombres,
            apellidos: ultimoTrabajador.apellidos
          };
          this.cargarEquiposActivosSiCorresponde();
        }
      },
      error: () => {
        this.buscandoTrabajador = false;
        this.trabajadorBuscado = true;
      }
    });
  }

  private validarFormulario(): boolean {
    if (!this.form.rut || this.form.rut.trim().length === 0) {
      return false;
    }
    if (!this.form.esExtranjero) {
      const rutLimpio = cleanRut(this.form.rut);
      if (!validateRut(rutLimpio)) {
        return false;
      }
    }
    if (!this.form.tipoMovimiento) {
      return false;
    }
    if (this.form.tipoMovimiento === 'REEMPLAZO' && !this.equipoAnteriorSeleccionado) {
      return false;
    }
    if (this.form.tipoMovimiento === 'DEVOLUCION' && !this.equipoSeleccionado) {
      return false;
    }
    return true;
  }

  generarLink() {
    if (this.form.tipoMovimiento === 'REEMPLAZO' && !this.equipoAnteriorSeleccionado) {
      this.alert.error_small('Para un REEMPLAZO debes indicar qué equipo activo se está devolviendo.');
      return;
    }
    if (this.form.tipoMovimiento === 'DEVOLUCION' && !this.equipoSeleccionado) {
      this.alert.error_small('Para una DEVOLUCION debes indicar cuál equipo activo se está devolviendo.');
      return;
    }
    if (!this.validarFormulario()) {
      this.alert.error_small('El rut ingresado no es válido, revise el formato o marque "Es extranjero"');
      return;
    }

    const payload: GenerarEntregaEquipoLinkPayload = {
      rut: this.form.rut.trim(),
      esExtranjero: this.form.esExtranjero,
      tipoMovimiento: this.form.tipoMovimiento,
      fk_equipoTecnologico: this.equipoSeleccionado ? this.equipoSeleccionado.id : null,
      fk_equipoTecnologicoAnterior: this.equipoAnteriorSeleccionado ? this.equipoAnteriorSeleccionado.fk_equipoTecnologico : null,
      observacion: this.form.observacion ? this.form.observacion.trim() : undefined
    };

    this.generando = true;
    this.entregaEquipoLinkService.generar(payload).subscribe({
      next: (res) => {
        this.generando = false;
        if (res.success) {
          this.linkGenerado = `${window.location.origin}${window.location.pathname}#/firma-entrega/${res.token}`;
          this.alert.success_small(res.msg || 'Link generado exitosamente');
          this.limpiarFormulario();
          this.linkGenerado_evento.emit(true);
        } else {
          this.alert.error_small(res.msg || 'No se pudo generar el link');
        }
      },
      error: () => {
        this.generando = false;
      }
    });
  }

  onHojaFisicaSeleccionada(event: Event) {
    const input = event.target as HTMLInputElement;
    this.hojaFisicaFile = input.files && input.files.length > 0 ? input.files[0] : null;
  }

  registrarManual() {
    if (this.form.tipoMovimiento === 'REEMPLAZO' && !this.equipoAnteriorSeleccionado) {
      this.alert.error_small('Para un REEMPLAZO debes indicar qué equipo activo se está devolviendo.');
      return;
    }
    if (this.form.tipoMovimiento === 'DEVOLUCION' && !this.equipoSeleccionado) {
      this.alert.error_small('Para una DEVOLUCION debes indicar cuál equipo activo se está devolviendo.');
      return;
    }
    if (!this.validarFormulario()) {
      this.alert.error_small('El rut ingresado no es válido, revise el formato o marque "Es extranjero"');
      return;
    }
    if (!this.hojaFisicaFile) {
      this.alert.error_small('Debes adjuntar el documento físico escaneado.');
      return;
    }

    const formData = new FormData();
    formData.append('rut', this.form.rut.trim());
    formData.append('esExtranjero', String(this.form.esExtranjero));
    formData.append('tipoMovimiento', this.form.tipoMovimiento);
    if (this.equipoSeleccionado) {
      formData.append('fk_equipoTecnologico', String(this.equipoSeleccionado.id));
    }
    if (this.equipoAnteriorSeleccionado) {
      formData.append('fk_equipoTecnologicoAnterior', String(this.equipoAnteriorSeleccionado.fk_equipoTecnologico));
    }
    if (this.form.observacion) {
      formData.append('observacion', this.form.observacion.trim());
    }
    formData.append('hojaFisica', this.hojaFisicaFile);

    this.registrandoManual = true;
    this.entregaEquipoLinkService.registrarManual(formData).subscribe({
      next: (res) => {
        this.registrandoManual = false;
        if (res.success) {
          this.alert.success_small(res.msg || 'Documento registrado correctamente');
          this.limpiarFormulario();
          this.linkGenerado_evento.emit(true);
        } else {
          this.alert.error_small(res.msg || 'No se pudo registrar el documento');
        }
      },
      error: () => { this.registrandoManual = false; }
    });
  }

  registrarForzado() {
    if (this.form.tipoMovimiento === 'REEMPLAZO' && !this.equipoAnteriorSeleccionado) {
      this.alert.error_small('Para un REEMPLAZO debes indicar qué equipo activo se está devolviendo.');
      return;
    }
    if (this.form.tipoMovimiento === 'DEVOLUCION' && !this.equipoSeleccionado) {
      this.alert.error_small('Para una DEVOLUCION debes indicar cuál equipo activo se está devolviendo.');
      return;
    }
    if (!this.validarFormulario()) {
      this.alert.error_small('El rut ingresado no es válido, revise el formato o marque "Es extranjero"');
      return;
    }
    if (!this.equipoSeleccionado) {
      this.alert.error_small('Debes seleccionar el equipo desde el catálogo (esta opción no crea equipos nuevos).');
      return;
    }
    if (!this.motivoSinFirma || this.motivoSinFirma.trim().length === 0) {
      this.alert.error_small('Debes indicar el motivo por el que no hay firma.');
      return;
    }

    const body: any = {
      rut: this.form.rut.trim(),
      esExtranjero: this.form.esExtranjero,
      tipoMovimiento: this.form.tipoMovimiento,
      fk_equipoTecnologico: this.equipoSeleccionado.id,
      motivoSinFirma: this.motivoSinFirma.trim(),
      observacion: this.form.observacion ? this.form.observacion.trim() : undefined
    };
    if (this.equipoAnteriorSeleccionado) {
      body.fk_equipoTecnologicoAnterior = this.equipoAnteriorSeleccionado.fk_equipoTecnologico;
    }

    this.registrandoForzado = true;
    this.entregaEquipoLinkService.registrarForzado(body).subscribe({
      next: (res) => {
        this.registrandoForzado = false;
        if (res.success) {
          this.alert.success_small(res.msg || 'Movimiento registrado sin firma correctamente');
          this.limpiarFormulario();
          this.linkGenerado_evento.emit(true);
        } else {
          this.alert.error_small(res.msg || 'No se pudo registrar el movimiento');
        }
      },
      error: () => { this.registrandoForzado = false; }
    });
  }

  copiarLink() {
    if (!this.linkGenerado) {
      return;
    }
    navigator.clipboard.writeText(this.linkGenerado).then(() => {
      this.alert.success_small('Link copiado al portapapeles');
    });
  }

  limpiarFormulario() {
    this.form = {
      rut: '',
      esExtranjero: false,
      tipoMovimiento: 'ENTREGA',
      observacion: ''
    };
    this.equipoSeleccionado = null;
    this.equiposEncontrados = [];
    this.textoBusquedaEquipo = '';
    this.trabajadorEncontrado = null;
    this.trabajadorBuscado = false;
    this.sugerenciasTrabajador = [];
    this.equiposActivosDelTrabajador = [];
    this.equipoAnteriorSeleccionado = null;
    this.esDocumentoManual = false;
    this.hojaFisicaFile = null;
    this.esRegistroForzado = false;
    this.motivoSinFirma = '';
  }

}
